// Fullscreen photo viewer. Rendered via portal to <body> because page
// containers use transform animations, which would otherwise capture
// position:fixed and push the overlay out of the viewport.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

import './PhotoLightbox.css';

const PhotoLightbox = ({ images, initialIndex = 0, alt = '', onClose }) => {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Trap the phone/browser back button: push a history entry on open so the
  // first "back" closes the preview instead of leaving the page.
  useEffect(() => {
    window.history.pushState({ jbLightbox: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed via X/backdrop/Escape: consume the entry we pushed
      if (window.history.state?.jbLightbox) window.history.back();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!images?.length) return null;

  return createPortal(
    <div
      className="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
    >
      <button
        className="photo-lightbox-close"
        type="button"
        aria-label="Close photo viewer"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        ✕
      </button>

      <div className="photo-lightbox-card">
        <Swiper
          modules={[Pagination]}
          initialSlide={initialIndex}
          slidesPerView={1}
          spaceBetween={24}
          pagination={{ clickable: true }}
          className="photo-lightbox-swiper"
        >
        {images.map((url, idx) => (
          <SwiperSlide key={`lb-${url}-${idx}`}>
            <div
              className="photo-lightbox-slide"
              onClick={(e) => {
                // Tap on the dark area closes; tap on the photo doesn't
                e.stopPropagation();
                if (e.target === e.currentTarget) onClose();
              }}
            >
              <img
                src={url}
                alt={`${alt} - full view ${idx + 1}`}
                onError={(ev) => {
                  if (ev.currentTarget.dataset.fallback === '1') return;
                  ev.currentTarget.dataset.fallback = '1';
                  ev.currentTarget.src = '/fallback-mobile.png';
                }}
              />
            </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>,
    document.body
  );
};

export default PhotoLightbox;
